// @flow
import React, { Component } from "react";
import { connect } from "react-redux";
import { bindAll, get, isEqual } from "lodash";
import { Containers } from "@tvg/atomic-ui/_static/BaseComponents";
import TrackUpcomingRaces from "@tvg/atomic-ui/_templates/UpcomingRaces";
import TopUpcomingRaces from "@tvg/upcoming-races";
import ApolloContext from "@tvg/utils/apolloContext";
import tvgConf from "@tvg/conf";
import { type Dispatch } from "redux";
import type { RouterHistory } from "react-router-dom";
import type { RaceStatusEnum, RaceVideo } from "@tvg/types/Race";
import TrackInfoComponent from "@tvg/tracks/src/v2/components/track-individual-info";
import { type State as Store } from "../../reducers";
import type { TransitionState } from "../../types";
import perf from "../utils/perf";
import {
  failMessagesRequest,
  successSeoMessagesRequest
} from "../../actions/capi";
import { setSeoTrackList, type SeoTrackList } from "../../actions/app";

export type NextRaces = {
  id: string,
  trackName: string,
  trackCode: string,
  postTime: string,
  number: string,
  status: {
    code: RaceStatusEnum
  },
  mtp: number,
  video: RaceVideo
};

type Props = {
  router: {
    history: RouterHistory
  },
  content: string,
  transitionState: TransitionState,
  races: NextRaces[],
  tracks?: SeoTrackList[],
  wagerProfile: string,
  dispatch: Dispatch<*>
};

type State = {
  upNextLoaded: boolean,
  isLoading: boolean,
  isLoadingContent: boolean
};

let loadingMaskTimeout;

export const getTrackAbbr = (history: RouterHistory): string => {
  const pathName = get(history, "location.pathname");

  if (pathName && /^\/racetracks\/[^/]+\/?.*$/.test(pathName)) {
    return pathName.slice(1).split("/")[1].toUpperCase();
  }

  return "NONE";
};

export class TrackPage extends Component<Props, State> {
  static defaultProps = {
    transitionState: "",
    races: []
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      upNextLoaded: false,
      isLoading: true,
      isLoadingContent: props.content === ""
    };
    bindAll(this, "signalLoadedComponent", "settingLoadingStateToOff");
    // Just in case any of: tracks AZ, favorites or up next doesn't load after 5 seconds
    loadingMaskTimeout = setTimeout(() => {
      this.setState({ ...this.state, isLoading: false });
    }, 5000);
  }

  componentDidMount() {
    if (this.props.content === "") {
      const nameSpace = ["SeoData"];
      const trackAbbr = get(this.props, "router.location.pathname")
        .slice(1)
        .split("/")[1]
        .toUpperCase();
      const trackHubName = `raceTrackHub${trackAbbr}`;

      tvgConf()
        .getMessages(nameSpace)
        .then((response) => {
          const seoContent = typeof response === "object" ? response : {};
          const trackContent = get(seoContent, `${trackHubName}`);

          this.setState({ ...this.state, isLoadingContent: false });

          if (trackContent) {
            this.props.dispatch(
              successSeoMessagesRequest(trackHubName, trackContent)
            );
          }
        })
        .catch((err: Error) => {
          this.setState({ ...this.state, isLoadingContent: false });
          this.props.dispatch(failMessagesRequest(err));
        });
    }
  }

  shouldComponentUpdate = (nextProps: Props, nextState: State) =>
    (nextProps.transitionState === "entered" &&
      this.props.transitionState !== "entered") ||
    this.state.isLoading !== nextState.isLoading ||
    nextProps.router.history.location !== this.props.router.history.location ||
    nextProps.content !== this.props.content ||
    !isEqual(this.props.races, nextProps.races);

  componentDidUpdate() {
    if (this.props.transitionState === "entered") {
      perf.measure("page_render", { page: "trackpage" });
    }
  }

  settingLoadingStateToOff() {
    if (this.state.upNextLoaded) {
      if (loadingMaskTimeout) {
        clearTimeout(loadingMaskTimeout);
        loadingMaskTimeout = undefined;
      }
      this.setState({ isLoading: false });
    }
  }

  signalLoadedComponent(componentLoaded: string): void {
    if (componentLoaded === "upNext") {
      this.setState({ upNextLoaded: true }, this.settingLoadingStateToOff);
    }
  }

  renderNextRaces = (rdaClientValue: mixed) => {
    const trackCode = getTrackAbbr(this.props.router.history);
    const trackNextRaces = this.props.races
      .filter((race) => race.trackCode === trackCode)
      .map((race) => ({
        raceNumber: race.number,
        raceStatus: race.status.code,
        mtp: race.mtp,
        raceId: race.id,
        trackName: race.trackName,
        trackCode: race.trackCode,
        postTime: race.postTime,
        video: race.video,
        country: "",
        isGreyhound: false,
        runnerNumber: "",
        talentPicks: []
      }));

    if (trackNextRaces.length > 0) {
      return (
        <TrackUpcomingRaces races={trackNextRaces} renderAllRacesBtn={false} />
      );
    }

    return (
      <TopUpcomingRaces
        shouldUpdate={this.props.transitionState === "entered"}
        signalLoadedComponent={this.signalLoadedComponent}
        rdaClient={rdaClientValue}
        parentPage="trackinfo"
      />
    );
  };

  render() {
    return (
      <ApolloContext.Consumer>
        {(value) => (
          <Containers.Page isFixed={false} data-qa-label="seoTrackInfo">
            <TrackInfoComponent
              device={tvgConf().device}
              fcpClient={value.fcpClient}
              rdaClient={value.rdaClient}
              tracksData={this.props.tracks}
              wagerProfile={this.props.wagerProfile}
              content={this.props.content}
              isLoadingContent={this.state.isLoadingContent}
              shouldUpdate={this.props.tracks?.length === 0}
              setTracksData={setSeoTrackList}
            />
          </Containers.Page>
        )}
      </ApolloContext.Consumer>
    );
  }
}

export const mapStateToProps = (store: Store, ownProps: Props) => {
  const trackAbbr = getTrackAbbr(ownProps.router.history);
  const defaultMessage = get(store, "capi.seoMessages.raceTrackHubDEF", "");

  return {
    content: get(
      store,
      `capi.seoMessages.raceTrackHub${trackAbbr}`,
      defaultMessage
    ),
    wagerProfile: get(store, "userData.user.profile", "PORT-Generic"),
    races: get(store, `mtpStatus.mtpStatus`, []),
    tracks: get(store, `app.seoTrackList`, [])
  };
};

export default connect(mapStateToProps)(TrackPage);
